<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper';
import SidebarProfileMenu from 'dashboard/components-next/sidebar/SidebarProfileMenu.vue';

const emit = defineEmits(['openKeyShortcutModal']);

const { t } = useI18n();

// Logo do branding (mesma fonte do Sidebar), com fallback para os assets
// padrão. Variante clara/escura trocada por classe conforme o tema.
const globalConfig = useMapGetter('globalConfig/get');
const logoLight = computed(() => globalConfig.value?.logo || '/logo.png');
const logoDark = computed(
  () => globalConfig.value?.logoDark || '/logodark.png'
);
const accountId = useMapGetter('getCurrentAccountId');
const homeLink = computed(() => `/app/accounts/${accountId.value}/dashboard`);

// Empresa (conta) logada — exibida entre o tema e o perfil na topbar.
const currentAccount = useMapGetter('getCurrentAccount');
const companyName = computed(() => currentAccount.value?.name || '');

// Non-translatable keyboard hint (rendered via binding to satisfy i18n lint).
const isMacPlatform = navigator.platform?.toUpperCase().includes('MAC');
const shortcutLabel = isMacPlatform ? '⌘K' : 'Ctrl K';

// Theme (light/dark) — self-contained via LocalStorage + setColorTheme,
// mirroring how the app applies the `.dark` class on boot.
const prefersDark = () =>
  window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
const isDark = ref(document.body.classList.contains('dark'));

const toggleTheme = () => {
  const next = isDark.value ? 'light' : 'dark';
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, next);
  setColorTheme(prefersDark());
  isDark.value = document.body.classList.contains('dark');
};

// Abre a paleta de comandos ⌘K. O keydown sintético não dispara o ninja-keys
// (ele escuta o evento nativo), então chamamos direto o método .open() do
// custom element, como o resto do app faz (ex.: ResolveAction.vue).
const openCommandBar = () => {
  document.querySelector('ninja-keys')?.open();
};
</script>

<template>
  <header
    class="flex items-center flex-shrink-0 gap-3 h-14 px-4 bg-n-solid-2 border-b border-n-weak"
  >
    <a
      :href="homeLink"
      class="flex items-center flex-shrink-0 ltr:mr-1 rtl:ml-1"
    >
      <img :src="logoLight" class="h-7 w-auto dark:hidden" alt="Logo" />
      <img :src="logoDark" class="hidden h-7 w-auto dark:block" alt="Logo" />
    </a>

    <button
      type="button"
      class="flex items-center gap-2 h-9 w-full sm:w-96 max-w-md px-3 rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 transition-colors text-sm"
      @click="openCommandBar"
    >
      <span class="flex-shrink-0 i-lucide-search size-4" />
      <span class="truncate">{{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}</span>
      <span
        class="ltr:ml-auto rtl:mr-auto font-mono text-xs text-n-slate-10 border border-n-weak rounded px-1.5 py-0.5"
      >
        {{ shortcutLabel }}
      </span>
    </button>

    <div class="flex items-center gap-1.5 ltr:ml-auto rtl:mr-auto">
      <button
        type="button"
        :title="isDark ? 'Tema claro' : 'Tema escuro'"
        class="flex items-center justify-center size-9 rounded-lg text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
        @click="toggleTheme"
      >
        <span v-if="isDark" class="i-lucide-sun size-4" />
        <span v-else class="i-lucide-moon size-4" />
      </button>

      <div class="w-px h-5 mx-1 bg-n-weak" />

      <span
        v-if="companyName"
        :title="companyName"
        class="hidden md:block max-w-[12rem] truncate text-sm font-medium text-n-slate-11"
      >
        {{ companyName }}
      </span>

      <div v-if="companyName" class="hidden md:block w-px h-5 mx-1 bg-n-weak" />

      <SidebarProfileMenu
        variant="topbar"
        @open-key-shortcut-modal="emit('openKeyShortcutModal')"
      />
    </div>
  </header>
</template>
